import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ChevronDownIcon = () => (
  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const Navbar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const isCurrentPage = (href) => {
    if (!href || href.startsWith('http')) return false;
    if (href === '/') return currentPath === '/';
    return currentPath === href || currentPath.startsWith(href + '/');
  };
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const [mobileOpenSubDropdown, setMobileOpenSubDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = Object.values(dropdownRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutside) {
        setOpenDropdown(null);
        setOpenSubDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    {
      label: 'Home',
      dropdown: [
        { label: 'About PS 96',            href: '/about-us' },
        { label: 'Mission and Vision',    href: '/mission-statement' },
        { label: 'Announcements',         href: '/announcements' },
        { label: 'Principal\'s Message',  href: '/principal-message' },
        { label: 'Staff Directory',       href: '/directory/faculty' },
        { label: 'Picture Gallery',       href: '/picture-gallery' },
      ]
    },
    {
      label: 'Academics',
      dropdown: [
        { label: 'Admissions',              href: '/early-childhood' },
        { label: 'Dual Language Programs',  href: '/dual_language_programs' },
        { label: 'Our Students',            href: '/our-students' },
        { label: 'Special Subjects',        href: '/special-subjects' },
      ]
    },
    {
      label: 'Student Life',
      dropdown: [
        { label: 'Student Council',   href: '/student-council' },
        { label: 'Sports @ 96',       href: '/sports' },
        { label: 'Girls Inc.',        href: '/girls-inc' },
        { label: 'Green Team',        href: '/green-team' },
        { label: 'School Spirit',     href: '/school-spirit' },
      ]
    },
    {
      label: 'Families',
      dropdown: [
        { label: 'Partnerships',          href: '/partnerships' },
        { label: 'MySchools Account',     href: 'https://mystudent.nyc/', external: true },
        { label: 'Food Pantry',           href: '/food-pantry' },
        { label: 'School Lunch Menu',     href: 'https://www.schools.nyc.gov/school-life/school-meals/menus/school-lunch-meals?value=s8', external: true },
        { label: 'Attendance Policy',     href: '/attendance-policy' },
        { label: 'After School',          href: '/after-school-programs' },
        { label: 'School Calendar',       href: '/calendar' },
        { label: 'Event Flyers',          href: '/event-flyers' },
        { label: 'Parent Handbook',       href: '/parent-handbook' },
      ]
    },
    {
      label: 'Resources',
      dropdown: [
        { label: 'Forms and Documents',        href: '/forms-documents' },
        { label: 'Contact Us',                 href: '/contact-us' },
        { label: 'School Info & Map',          href: '/directions' },
        { label: 'Sign Up for Emails',         href: '/email-signup' },
        { label: 'Frequently Asked Questions', href: 'https://docs.google.com/document/d/1K39AKs00SNGaK4ReyjYOT3SbQSTWn0GGFDU7m0NxTWw/edit?usp=sharing', external: true },
        { label: 'Order Uniforms',             href: 'https://stores.smoothusa.com/PS96ACT/shop/home', external: true },
      ]
    },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Skip to content - visible on focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-white focus:text-gray-900 focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#00213a] focus:ring-offset-2"
      >
        Skip to main content
      </a>

      <div className="mx-auto max-w-[920px] px-4">
        <nav
          className={`relative mt-3 rounded-2xl bg-white border border-gray-200 transition-shadow duration-200 overflow-visible ${
            isScrolled ? 'shadow-md' : 'shadow-sm'
          }`}
          aria-label="Primary"
        >
          <div className="flex items-center h-14 px-5">
            {/* Logo */}
            <a href="/" className="flex-shrink-0 mr-4" aria-label="PS 96 Joseph C. Lanzetta School — Home">
              <img
                src="/assets/images/ps96act_logo.png"
                alt="PS 96 Joseph C. Lanzetta School Logo"
                className="h-9 w-auto"
              />
            </a>

            {/* Desktop Navigation — centered */}
            <div className="hidden lg:flex flex-1 items-center justify-center gap-0.5">
              {navItems.map((item, index) => {
                const hasDropdown = item.dropdown && item.dropdown.length > 0;

                if (!hasDropdown) {
                  const active = isCurrentPage(item.href);
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      aria-current={active ? 'page' : undefined}
                      className="px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-[#00213a] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00213a] focus-visible:ring-offset-2 rounded-lg whitespace-nowrap"
                    >
                      {item.label}
                    </a>
                  );
                }

                const parentActive = item.dropdown.some((d) => isCurrentPage(d.href));
                return (
                  <div
                    key={item.label}
                    className="relative"
                    ref={(el) => (dropdownRefs.current[index] = el)}
                    onMouseEnter={() => setOpenDropdown(index)}
                    onMouseLeave={() => {
                      setOpenDropdown(null);
                      setOpenSubDropdown(null);
                    }}
                  >
                    <button
                      className="inline-flex items-center px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-[#00213a] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00213a] focus-visible:ring-offset-2 rounded-lg whitespace-nowrap"
                      aria-expanded={openDropdown === index}
                      aria-haspopup="true"
                      aria-current={parentActive ? 'page' : undefined}
                    >
                      {item.label}
                      <ChevronDownIcon />
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdown === index && (
                      <div className="absolute left-0 top-full pt-2 w-64 z-50">
                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 py-2">
                        {item.dropdown.map((dropdownItem, dropdownIndex) => {
                          const hasSubDropdown = dropdownItem.subDropdown && dropdownItem.subDropdown.length > 0;

                          if (!hasSubDropdown) {
                            const ddActive = isCurrentPage(dropdownItem.href);
                            return (
                              <a
                                key={dropdownItem.label}
                                href={dropdownItem.href}
                                target={dropdownItem.external ? '_blank' : undefined}
                                rel={dropdownItem.external ? 'noopener noreferrer' : undefined}
                                aria-current={ddActive ? 'page' : undefined}
                                className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#00213a] transition-colors duration-150"
                              >
                                {dropdownItem.label}
                              </a>
                            );
                          }

                          return (
                            <div
                              key={dropdownItem.label}
                              className="relative"
                              onMouseEnter={() => setOpenSubDropdown(`${index}-${dropdownIndex}`)}
                              onMouseLeave={() => setOpenSubDropdown(null)}
                            >
                              <button
                                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#00213a] transition-colors duration-150 text-left"
                                aria-expanded={openSubDropdown === `${index}-${dropdownIndex}`}
                                aria-haspopup="true"
                              >
                                {dropdownItem.label}
                                <ChevronRightIcon />
                              </button>

                              {/* Sub-dropdown Menu */}
                              {openSubDropdown === `${index}-${dropdownIndex}` && (
                                <div className="absolute left-full top-0 pl-2 w-56 z-50">
                                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 py-2">
                                    {dropdownItem.subDropdown.map((subItem) => (
                                      <a
                                        key={subItem.label}
                                        href={subItem.href}
                                        className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#00213a] transition-colors duration-150"
                                      >
                                        {subItem.label}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Social icons — desktop */}
            <div className="hidden lg:flex items-center gap-1 ml-3 flex-shrink-0">
              <a
                href="https://www.instagram.com/ps96act/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PS 96 on Instagram"
                className="p-1.5 rounded-lg text-gray-500 hover:text-[#E1306C] hover:bg-gray-100 transition-colors duration-150"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a
                href="http://www.facebook.com/PS96Lions"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PS 96 on Facebook"
                className="p-1.5 rounded-lg text-gray-500 hover:text-[#1877F2] hover:bg-gray-100 transition-colors duration-150"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>

            {/* Language selector + mobile menu button */}
            <div className="flex items-center gap-1 ml-auto lg:ml-3 flex-shrink-0">
              <span id="site-language" className="inline-flex items-center"></span>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00213a] focus-visible:ring-offset-2"
                aria-expanded={mobileMenuOpen}
                aria-label="Toggle menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-100 px-4 py-4 space-y-1 bg-white max-h-[calc(100vh-100px)] overflow-y-auto">
              {navItems.map((item, index) => {
                const hasDropdown = item.dropdown && item.dropdown.length > 0;

                if (!hasDropdown) {
                  const active = isCurrentPage(item.href);
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      aria-current={active ? 'page' : undefined}
                      className="block px-3 py-2.5 text-base font-medium text-gray-700 hover:text-[#00213a] hover:bg-gray-50 rounded-lg transition-colors duration-150"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  );
                }

                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => setMobileOpenDropdown(mobileOpenDropdown === index ? null : index)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-base font-medium text-gray-700 hover:text-[#00213a] hover:bg-gray-50 rounded-lg transition-colors duration-150"
                      aria-expanded={mobileOpenDropdown === index}
                    >
                      {item.label}
                      <ChevronDownIcon />
                    </button>

                    {/* Mobile Dropdown */}
                    {mobileOpenDropdown === index && (
                      <div className="pl-4 space-y-1">
                        {item.dropdown.map((dropdownItem, dropdownIndex) => {
                          const hasSubDropdown = dropdownItem.subDropdown && dropdownItem.subDropdown.length > 0;

                          if (!hasSubDropdown) {
                            const ddActive = isCurrentPage(dropdownItem.href);
                            return (
                              <a
                                key={dropdownItem.label}
                                href={dropdownItem.href}
                                target={dropdownItem.external ? '_blank' : undefined}
                                rel={dropdownItem.external ? 'noopener noreferrer' : undefined}
                                aria-current={ddActive ? 'page' : undefined}
                                className="block px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#00213a] hover:bg-gray-50 rounded-lg transition-colors duration-150"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {dropdownItem.label}
                              </a>
                            );
                          }

                          return (
                            <div key={dropdownItem.label} className="space-y-1">
                              <button
                                onClick={() => {
                                  const key = `${index}-${dropdownIndex}`;
                                  setMobileOpenSubDropdown(mobileOpenSubDropdown === key ? null : key);
                                }}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#00213a] hover:bg-gray-50 rounded-lg transition-colors duration-150"
                                aria-expanded={mobileOpenSubDropdown === `${index}-${dropdownIndex}`}
                              >
                                {dropdownItem.label}
                                <ChevronDownIcon />
                              </button>

                              {/* Mobile Sub-dropdown */}
                              {mobileOpenSubDropdown === `${index}-${dropdownIndex}` && (
                                <div className="pl-4 space-y-1">
                                  {dropdownItem.subDropdown.map((subItem) => (
                                    <a
                                      key={subItem.label}
                                      href={subItem.href}
                                      className="block px-3 py-2 text-sm font-medium text-gray-500 hover:text-[#00213a] hover:bg-gray-50 rounded-lg transition-colors duration-150"
                                      onClick={() => setMobileMenuOpen(false)}
                                    >
                                      {subItem.label}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Social icons — mobile */}
              <div className="flex items-center gap-3 px-3 pt-3 pb-1 border-t border-gray-100 mt-2">
                <a
                  href="https://www.instagram.com/ps96act/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PS 96 on Instagram"
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#E1306C] transition-colors duration-150"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                  Instagram
                </a>
                <span className="text-gray-500" aria-hidden="true">·</span>
                <a
                  href="http://www.facebook.com/PS96Lions"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PS 96 on Facebook"
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1877F2] transition-colors duration-150"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
